import { track } from "../../clients/mixpanel";
import { Room, AssociatedKey } from "../../interfaces/Room";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { LineItem } from "../../interfaces/LineItem";
import { getEstimateReportData, setPrintAreaToUsedRange } from "../workbook";
import { ValidationError } from "../../errors";
import { ColumnNameToIndexMap } from "./helpers/common";
import { CellOverride, CellStyle, RowFormat, createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { handleDynamicReportGeneration, SheetDataItem } from "./helpers/dynamicSheets";
import { cacheableData } from "../workbook/cache";
import { rowNames } from "./helpers/rangeNames";
import { FinishKey, RoomGroup } from "../../interfaces/Room";
import { fetchEstimateExport } from "../../api/estimates";
import { getEstimateFinishKeys, getEstimateRoomGroups, getEstimateRooms } from "../datasources/rooms";
import { transformUseGroups } from "../datasources/useGroups";
import { ROOMS_REPORT_NAME } from "./api/reportNames";

export interface RoomsReportOptions extends cacheableData {
  // Add any specific options if needed
}

interface RoomData {
  id: string;
  label: string;
  perimeter: number;
  areaAvg: number;
  quantity: number;
  height: number;
  areaTotal: number;
  totalCost: number;
  walls: string; // Formatted string like "PT-1: 100%"
  floor: string;
  ceiling: string;
  base: string;
  wallKeys: AssociatedKey[];
  floorKeys: AssociatedKey[];
  ceilingKeys: AssociatedKey[];
  baseKeys: AssociatedKey[];
  created_at: string;
  roomGroupName: string;
}

interface FinishSummaryData {
  mark: string;
  description: string;
  quantity: number;
  uom: string;
  unitCost: number;
}

export const roomsDefaultOptions = {} as RoomsReportOptions;

const ROOM_REPORT_COLUMN_KEYS = [
  "room_name",
  "perimeter",
  "area_avg",
  "room_qty",
  "room_height",
  "area_total",
  "total_cost",
  "walls",
  "floor_finish",
  "ceiling",
  "base",
] as const;

const FINISH_SUMMARY_COLUMN_KEYS = ["mark", "description", "finish_qty", "finish_uom", "finish_unit_cost"] as const;

/**
 * Fetches rooms with use group name for the current estimate
 */
export const fetchRoomsWithUseGroupName = async (estimateIdParam?: string): Promise<Room[]> => {
  let estimateId = estimateIdParam;

  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) return [];

  const response = await fetchEstimateExport(estimateId);
  if (!response.success) throw response.error;
  return response.data ? getEstimateRooms(response.data) : [];
};

/**
 * Formats associated keys into display string (e.g., "PT-1: 100%")
 */
const formatAssociatedKeys = (keys: AssociatedKey[], finishKeysMap: Map<string, FinishKey>): string => {
  if (!keys || keys.length === 0) return "";

  return keys
    .map((key) => {
      const finishKey = finishKeysMap.get(key.id);
      const mark = finishKey?.mark || key.id.slice(0, 6);
      return `${mark}: ${Math.round(key.percentage)}%`;
    })
    .join(", ");
};

/**
 * Maps finish-schedule fixture id to its line item's unit cost, so a room's fixtures can be costed
 * from `room.extras.fixtures` alone (fixtures carry only the line item reference, not the cost).
 */
const buildFixtureCostMap = (fixtures: unknown[], lineItems: LineItem[]): Map<string, number> => {
  const unitCostByLineItemId = new Map<string, number>();
  lineItems.forEach((lineItem) => {
    unitCostByLineItemId.set(lineItem.id, lineItem.total_uc || 0);
  });

  const fixtureCostMap = new Map<string, number>();
  (fixtures as { id?: string; line_item?: string }[]).forEach((fixture) => {
    if (!fixture?.id || !fixture.line_item) return;
    fixtureCostMap.set(fixture.id, unitCostByLineItemId.get(fixture.line_item) || 0);
  });

  return fixtureCostMap;
};

/**
 * Cost per unit of geometry for one finish part, summed across its associated keys. Callers
 * multiply by the part's geometry (perimeter, area, or perimeter x height) and the room quantity.
 */
const finishPartCostPerUnit = (keys: AssociatedKey[], finishKeysMap: Map<string, FinishKey>): number =>
  keys.reduce((total, key) => {
    const unitCost = finishKeysMap.get(key.id)?.line_item?.total_uc || 0;
    return total + unitCost * (key.percentage / 100);
  }, 0);

/**
 * Main entry point for generating Rooms reports (multi-sheet by use group)
 */
export const generateRoomsReports = async (context: Excel.RequestContext, templateWorksheet: Excel.Worksheet) => {
  const startTime = performance.now();

  // Get estimate ID from the Report table
  const reportData = (await getEstimateReportData()) as any[];
  if (reportData.length === 0) {
    throw new ValidationError("No estimate loaded. Please load an estimate first.");
  }

  const estimateId = reportData[0].estimate_id;
  if (!estimateId) {
    throw new ValidationError("Estimate ID is required to generate Rooms report.");
  }

  const estimateResult = await fetchEstimateExport(estimateId);
  if (!estimateResult.success || !estimateResult.data) {
    throw new ValidationError("Failed to fetch rooms data for Rooms report.");
  }

  const rooms = getEstimateRooms(estimateResult.data);
  const finishKeys = getEstimateFinishKeys(estimateResult.data);
  const roomGroups = getEstimateRoomGroups(estimateResult.data);

  if (rooms.length === 0) {
    throw new ValidationError("Load an estimate with rooms before generating a Rooms report.");
  }

  // Build use groups from estimate data (avoids dependency on UseGroups table)
  const rawUseGroups =
    estimateResult.success && estimateResult.data ? estimateResult.data.estimate?.stats?.useGroups || [] : [];
  const floorGroups = estimateResult.success && estimateResult.data ? estimateResult.data.floor_groups || [] : [];
  const useGroups = transformUseGroups(rawUseGroups, floorGroups);
  useGroups.sort((a, b) => a.order - b.order);

  const useGroupDictionary = useGroups.reduce(
    (acc, useGroup) => {
      acc[useGroup.id] = useGroup.name;
      return acc;
    },
    {} as Record<string, string>
  );

  const fixtureCostMap =
    estimateResult.success && estimateResult.data
      ? buildFixtureCostMap(estimateResult.data.fixtures || [], estimateResult.data.line_items || [])
      : new Map<string, number>();

  const mappedRooms = rooms.map((room) => {
    return {
      ...room,
      estimate_use_group_name: useGroupDictionary[room.estimate_use_group],
    };
  });

  const sheets = await handleDynamicReportGeneration(
    context,
    templateWorksheet,
    ROOMS_REPORT_NAME,
    mappedRooms,
    "estimate_use_group",
    "estimate_use_group_name"
  );

  // For each sheet, call generateRoomsReport
  for (let i = 0; i < sheets.length; i++) {
    const sheetItem = sheets[i];
    await generateRoomsReport(sheetItem, useGroups, finishKeys, roomGroups, fixtureCostMap);
  }

  track("excel.createReport.rooms", {
    loadTime: (performance.now() - startTime) / 1000,
  });
};

/**
 * Generates a single Rooms report sheet
 */
export const generateRoomsReport = async (
  sheetData: SheetDataItem,
  useGroups: ReportUseGroup[],
  finishKeys: FinishKey[],
  roomGroups: RoomGroup[],
  fixtureCostMap: Map<string, number>
) => {
  await Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getItem(sheetData.sheetName);
    const rooms = sheetData.data as Room[];

    const useGroup = useGroups.find((ug) => ug.id === sheetData.key);
    if (!useGroup) {
      throw new ValidationError(`Use group ${sheetData.key} not found.`);
    }

    // Create finish keys map for quick lookup
    const finishKeysMap = new Map<string, FinishKey>();
    finishKeys.forEach((key) => {
      finishKeysMap.set(key.id, key);
    });

    // Create room groups map for quick lookup
    const roomGroupsMap = new Map<string, RoomGroup>();
    roomGroups.forEach((group) => {
      roomGroupsMap.set(group.id, group);
    });

    // Process room data
    const roomDataList: RoomData[] = rooms.map((room) => {
      const wallKeys = (room.walls as any)?.associatedKeys || [];
      const floorKeys = room.floor?.associatedKeys || [];
      const ceilingKeys = room.ceiling?.associatedKeys || [];
      const baseKeys = room.base?.associatedKeys || [];

      const roomGroup = room.room_group_id ? roomGroupsMap.get(room.room_group_id) : undefined;

      const perimeter = room.perimeter?.value || 0;
      const areaAvg = room.area_average?.value || 0;
      const quantity = room.quantity?.value || 0;
      const height = room.height || 0;

      const finishCost =
        finishPartCostPerUnit(wallKeys, finishKeysMap) * perimeter * height +
        finishPartCostPerUnit(floorKeys, finishKeysMap) * areaAvg +
        finishPartCostPerUnit(ceilingKeys, finishKeysMap) * areaAvg +
        finishPartCostPerUnit(baseKeys, finishKeysMap) * perimeter;
      const fixtureCost = (room.extras?.fixtures || []).reduce(
        (total, fixture) => total + (fixture?.value || 0) * (fixtureCostMap.get(fixture?.id) || 0),
        0
      );

      return {
        id: room.id,
        label: room.label,
        perimeter,
        areaAvg,
        quantity,
        height,
        areaTotal: quantity * areaAvg,
        totalCost: (finishCost + fixtureCost) * quantity,
        walls: formatAssociatedKeys(wallKeys, finishKeysMap),
        floor: formatAssociatedKeys(floorKeys, finishKeysMap),
        ceiling: formatAssociatedKeys(ceilingKeys, finishKeysMap),
        base: formatAssociatedKeys(baseKeys, finishKeysMap),
        wallKeys,
        floorKeys,
        ceilingKeys,
        baseKeys,
        created_at: room.created_at,
        roomGroupName: roomGroup?.custom_label || roomGroup?.name || "",
      };
    });

    // Sort rooms by room group name (ungrouped last), then alphabetically by label within each group
    roomDataList.sort((a, b) => {
      const aEmpty = a.roomGroupName === "";
      const bEmpty = b.roomGroupName === "";
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
      const groupCompare = a.roomGroupName.localeCompare(b.roomGroupName);
      if (groupCompare !== 0) return groupCompare;
      return a.label.localeCompare(b.label);
    });

    // Calculate finish summary data
    const finishSummary = calculateFinishSummary(roomDataList, finishKeysMap);

    const columnMap = await createColumnNameToIndexMap(worksheet);

    const { mappedArray, formatList } = roomsArray(roomDataList, finishSummary, columnMap);

    await printFormattedData({
      dataArray: mappedArray,
      formatList,
      worksheet,
      insertAtRangeName: rowNames.roomItems,
      startColumnIndex: 1,
      options: {},
    });

    await setPrintAreaToUsedRange(worksheet);
  });
};

/**
 * Calculates finish summary data from room data
 */
const calculateFinishSummary = (
  roomDataList: RoomData[],
  finishKeysMap: Map<string, FinishKey>
): FinishSummaryData[] => {
  const summaryMap = new Map<string, FinishSummaryData>();

  const processKeys = (keys: AssociatedKey[], area: number, isPerimeter: boolean = false) => {
    const uom = isPerimeter ? "lf" : "sf";
    keys.forEach((key) => {
      const finishKey = finishKeysMap.get(key.id);
      if (!finishKey) return;

      const quantity = (area * key.percentage) / 100;
      const summaryKey = `${key.id}_${uom}`;

      if (!summaryMap.has(summaryKey)) {
        summaryMap.set(summaryKey, {
          mark: finishKey.mark || key.id.slice(0, 6),
          description: finishKey.line_item?.description || finishKey.line_item?.name || "",
          quantity: 0,
          uom,
          unitCost: finishKey.line_item?.total_uc || 0,
        });
      }

      const summary = summaryMap.get(summaryKey)!;
      summary.quantity += quantity;
    });
  };

  roomDataList.forEach((room) => {
    const wallArea = room.perimeter * room.height * room.quantity;
    const floorArea = room.areaAvg * room.quantity;
    const ceilingArea = room.areaAvg * room.quantity;
    const baseLength = room.perimeter * room.quantity;

    processKeys(room.wallKeys, wallArea);
    processKeys(room.floorKeys, floorArea);
    processKeys(room.ceilingKeys, ceilingArea);
    processKeys(room.baseKeys, baseLength, true);
  });

  // Convert to array and sort by mark
  return Array.from(summaryMap.values()).sort((a, b) => a.mark.localeCompare(b.mark));
};

function roomColumnValue(room: RoomData, key: (typeof ROOM_REPORT_COLUMN_KEYS)[number]): string | number {
  switch (key) {
    case "room_name":
      return room.label;
    case "perimeter":
      return room.perimeter;
    case "area_avg":
      return room.areaAvg;
    case "room_qty":
      return room.quantity;
    case "room_height":
      return room.height;
    case "area_total":
      return room.areaTotal;
    case "total_cost":
      return room.totalCost;
    case "walls":
      return room.walls;
    case "floor_finish":
      return room.floor;
    case "ceiling":
      return room.ceiling;
    case "base":
      return room.base;
  }
}

function finishSummaryColumnValue(
  summary: FinishSummaryData,
  key: (typeof FINISH_SUMMARY_COLUMN_KEYS)[number]
): string | number {
  switch (key) {
    case "mark":
      return summary.mark;
    case "description":
      return summary.description;
    case "finish_qty":
      return Math.round(summary.quantity);
    case "finish_uom":
      return summary.uom;
    case "finish_unit_cost":
      return summary.unitCost;
  }
}

/**
 * Groups rooms by roomGroupName, preserving pre-sorted order.
 * Rooms with no group name are placed in an "Uncategorized Rooms" bucket at the end (sorted there by the caller).
 */
const groupRoomsByRoomGroup = (roomDataList: RoomData[]): { groupName: string; rooms: RoomData[] }[] => {
  const grouped = new Map<string, RoomData[]>();

  for (const room of roomDataList) {
    const key = room.roomGroupName || "";
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(room);
  }

  return Array.from(grouped.entries()).map(([groupName, rooms]) => ({ groupName, rooms }));
};

/**
 * Generates the report array data, grouping rooms by room group with section headers
 */
const roomsArray = (roomDataList: RoomData[], finishSummary: FinishSummaryData[], columnMap: ColumnNameToIndexMap) => {
  // Calculate total columns based on column map
  const maxColumnIndex = Math.max(...Object.values(columnMap).filter((v): v is number => typeof v === "number"));
  const totalColumns = maxColumnIndex + 1;

  const formatList: RowFormat[] = [];
  const mappedArray: (string | number)[][] = [];

  // Build the left-side room rows grouped by room group
  const roomGroups = groupRoomsByRoomGroup(roomDataList);
  const hasMultipleGroups = roomGroups.length > 1 || (roomGroups.length === 1 && roomGroups[0].groupName !== "");

  interface RoomRow {
    type: "header" | "item";
    room?: RoomData;
    groupName?: string;
  }
  const roomRows: RoomRow[] = [];

  for (const group of roomGroups) {
    if (hasMultipleGroups) {
      roomRows.push({ type: "header", groupName: group.groupName || "Uncategorized Rooms" });
    }
    for (const room of group.rooms) {
      roomRows.push({ type: "item", room });
    }
  }

  // The spacers sit immediately left of the finishes and finish-summary blocks. Deriving them from
  // the column map rather than fixed offsets keeps them correct on sheets whose layout differs.
  const firstSpacerIndex = columnMap["walls"] !== undefined ? columnMap["walls"] - 1 : undefined;
  const secondSpacerIndex = columnMap["mark"] !== undefined ? columnMap["mark"] - 1 : undefined;

  // Calculate the maximum number of rows (room rows including headers vs finish summary)
  const totalRoomRows = roomRows.length;
  const maxRows = Math.max(totalRoomRows, finishSummary.length);
  const isRoomDataShorter = totalRoomRows < finishSummary.length;
  const isFinishSummaryShorter = finishSummary.length < totalRoomRows;

  // Generate rows with room data on the left and finish summary on the right
  for (let i = 0; i < maxRows; i++) {
    const row: (string | number)[] = new Array(totalColumns).fill("");

    // Cell overrides for styling spacer columns (based on new layout)
    // Columns: B-H rooms, I spacer, J-M finishes, N spacer, O-S finish summary, T end spacer
    let firstSpacerStyle: CellStyle = "spacerOuterBorders";
    let secondSpacerStyle: CellStyle = "spacerOuterBorders";
    let endSpacerStyle: CellStyle = "spacerOuterBorderLeft";

    if (isFinishSummaryShorter && i > finishSummary.length - 1) {
      secondSpacerStyle = "spacerOuterBorderLeft";
      endSpacerStyle = "item";
    } else if (isRoomDataShorter && i > totalRoomRows - 1) {
      firstSpacerStyle = "item";
      secondSpacerStyle = "spacerOuterBorderRight";
    }

    const roomsCellOverrides: CellOverride[] = [
      ...(firstSpacerIndex === undefined ? [] : [{ columnIndex: firstSpacerIndex, style: firstSpacerStyle }]),
      ...(secondSpacerIndex === undefined ? [] : [{ columnIndex: secondSpacerIndex, style: secondSpacerStyle }]),
      { columnIndex: totalColumns, style: endSpacerStyle, width: 20 }, // end spacer, one past the last named column
    ];

    let rowStyle: CellStyle = "gridItem";

    // Room data (left side)
    if (i < totalRoomRows) {
      const roomRow = roomRows[i];
      if (roomRow.type === "header") {
        // Section header row for the room group
        if (columnMap["room_name"] !== undefined) {
          row[columnMap["room_name"]] = roomRow.groupName!;
        }
        rowStyle = "sectionHeader";
        // Prevent the sectionHeader style from leaking into the finish summary columns
        applyReportColumnOverrides(roomsCellOverrides, columnMap, FINISH_SUMMARY_COLUMN_KEYS, "gridItem");
      } else {
        const roomColumnValuePairs = ROOM_REPORT_COLUMN_KEYS.map((key) => ({
          key,
          value: roomColumnValue(roomRow.room!, key),
        }));
        applyReportColumnValues(row, columnMap, roomColumnValuePairs);
      }
    } else if (totalRoomRows < maxRows) {
      const style = i === totalRoomRows ? "spacerOuterBorderTop" : "item";
      applyReportColumnOverrides(roomsCellOverrides, columnMap, ROOM_REPORT_COLUMN_KEYS, style);
    }

    // Finish summary data (right side)
    if (i < finishSummary.length) {
      const finishSummaryColumnValuePairs = FINISH_SUMMARY_COLUMN_KEYS.map((key) => ({
        key,
        value: finishSummaryColumnValue(finishSummary[i], key),
      }));
      applyReportColumnValues(row, columnMap, finishSummaryColumnValuePairs);
    } else if (finishSummary.length < maxRows) {
      const style = i === finishSummary.length ? "spacerOuterBorderTop" : "item";
      applyReportColumnOverrides(roomsCellOverrides, columnMap, FINISH_SUMMARY_COLUMN_KEYS, style);
    }

    formatList.push({
      style: rowStyle,
      level: 0,
      indentColumn: undefined,
      group: [],
      cellOverrides: roomsCellOverrides,
      adjacentCellStyle:
        totalRoomRows < maxRows && i >= totalRoomRows ? { direction: "left", style: "item" } : undefined,
    });

    mappedArray.push(row);
  }

  // Add one more formatList item for the row below the last data row to add the outer border if the last data row is equal to maxRows
  const cellOverrides: CellOverride[] = [];
  if (totalRoomRows === maxRows) {
    applyReportColumnOverrides(cellOverrides, columnMap, ROOM_REPORT_COLUMN_KEYS, "spacerOuterBorderTop");
  }

  if (finishSummary.length === maxRows) {
    applyReportColumnOverrides(cellOverrides, columnMap, FINISH_SUMMARY_COLUMN_KEYS, "spacerOuterBorderTop");
  }

  formatList.push({
    style: "item",
    level: 0,
    indentColumn: undefined,
    group: [],
    cellOverrides,
  });

  return { mappedArray, formatList };
};

export const applyReportColumnValues = (
  row: (string | number)[],
  columnMap: ColumnNameToIndexMap,
  pairs: readonly { key: string; value: string | number }[]
): void => {
  for (const { key, value } of pairs) {
    const idx = columnMap[key];
    if (idx === undefined) continue;
    row[idx] = value;
  }
};

export const applyReportColumnOverrides = (
  overrides: CellOverride[],
  columnMap: ColumnNameToIndexMap,
  keys: readonly string[],
  style: CellStyle
): void => {
  for (const key of keys) {
    const idx = columnMap[key];
    if (idx === undefined) continue;
    overrides.push({
      columnIndex: idx,
      style,
    });
  }
};
